<template>
  <div>
    <v-row v-if="showActiveBanner && (checkingActive || (activeTournaments && activeTournaments.length > 0))">
      <v-col cols="12">
        <v-progress-linear v-if="checkingActive" indeterminate color="primary" class="mb-2">
        </v-progress-linear>
        <v-alert v-for="t in activeTournaments" :key="'active_' + t._id" type="success" dense outlined
          class="mb-2" style="cursor:pointer" @click="goToTournament(t)">
          <v-icon left size="18">mdi-run-fast</v-icon>
          {{ $t("message.competing_now_in", [t.name]) }}
          <v-icon right size="18">mdi-arrow-right</v-icon>
        </v-alert>
      </v-col>
    </v-row>

    <v-row class="text-center">
      <!-- Wins/losses are never shown aggregated — bjjmetrics.com's historical data has
           no match-level win/loss info, only final placement, so an aggregate total
           would silently mix "wins from the few tournaments we've live-tracked" with
           "no data at all" for everything sourced historically, implying a complete
           record that doesn't exist. Win/loss is only ever shown per event, in the
           results table, for tournaments we actually have match-level data for. -->
      <v-col cols="12">
        <div class="text-h5">{{ displayTotalTournaments }}</div>
        <div class="text-caption">{{ $t("message.tournaments") }}</div>
      </v-col>
    </v-row>

    <v-row v-if="displayMedalsByBelt.length > 0" class="mt-1 text-center">
      <v-col>
        <v-badge color="error" :content="String(totalMedals.bronze)" overlap>
          <IconCompMedal :color="themeColor('bronze')"></IconCompMedal>
        </v-badge>
      </v-col>
      <v-col>
        <v-badge color="error" :content="String(totalMedals.silver)" overlap>
          <IconCompMedal :color="themeColor('silver')"></IconCompMedal>
        </v-badge>
      </v-col>
      <v-col>
        <v-badge color="error" :content="String(totalMedals.gold)" overlap>
          <IconCompMedal :color="themeColor('gold')"></IconCompMedal>
        </v-badge>
      </v-col>
    </v-row>
    <v-row v-if="displayMedalsByBelt.length > 0">
      <v-col cols="12" class="text-center text-overline">
        {{ $t("message.total_medals") }}: {{ totalMedals.total }}
      </v-col>
      <v-col cols="12">
        <div class="text-subtitle-2 mb-1">{{ $t("message.medals_by_belt") }}</div>
        <v-chip-group column>
          <v-chip small outlined v-for="m in displayMedalsByBelt" :key="'medal_' + m.belt">
            <b class="mr-1">{{ m.belt }}:</b>
            <IconCompMedal :color="themeColor('bronze')" class="mx-1"></IconCompMedal>{{ m.bronze || 0 }}
            <IconCompMedal :color="themeColor('silver')" class="mx-1"></IconCompMedal>{{ m.silver || 0 }}
            <IconCompMedal :color="themeColor('gold')" class="mx-1"></IconCompMedal>{{ m.gold || 0 }}
          </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <v-row v-if="showAffiliationHistory && profile.affiliationHistory && profile.affiliationHistory.length > 0">
      <v-col cols="12">
        <div class="text-subtitle-2 mb-1">{{ $t("message.affiliation_history") }}</div>
        <v-timeline dense>
          <v-timeline-item small v-for="(a, index) in profile.affiliationHistory" :key="'aff_' + index">
            <b>{{ a.name }}</b>
            <div class="text-caption">{{ formatDate(a.firstSeenAt) }} - {{ formatDate(a.lastSeenAt) }}</div>
          </v-timeline-item>
        </v-timeline>
      </v-col>
    </v-row>
    <v-row v-else-if="currentAffiliation">
      <v-col cols="12">
        <div class="text-subtitle-2 mb-1">{{ $t("message.affiliation_generic") }}</div>
        <div>{{ currentAffiliation.name }}</div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import IconCompMedal from "@/components/displays/icons/IconCompMedal";

export default {
  name: "CompetitorStatsSummary",
  components: {
    IconCompMedal,
  },
  props: {
    profile: { type: Object, required: true },
    activeTournaments: { type: Array, default: () => [] },
    checkingActive: { type: Boolean, default: false },
    results: { type: Array, default: () => [] },
    showActiveBanner: { type: Boolean, default: true },
    showAffiliationHistory: { type: Boolean, default: true },
  },
  computed: {
    currentAffiliation() {
      const history = this.profile.affiliationHistory;
      if (!history || history.length === 0) return null;
      return history[history.length - 1];
    },
    // Active tournaments the competitor is fighting in right now but that don't yet
    // have a finalized IBJJFCompetitionResult (matched by tournamentId) — counting
    // these gives credit for the tournament immediately rather than waiting on
    // finalization, without risk of double counting once it does finalize (at which
    // point it drops out of activeTournaments and shows up via totalTournaments/results
    // instead).
    unrepresentedActiveTournaments() {
      const finalizedTournamentIds = new Set(
        (this.results || []).map((r) => String(r.tournamentId)).filter(Boolean)
      );
      return (this.activeTournaments || []).filter(
        (t) => !finalizedTournamentIds.has(String(t._id))
      );
    },
    // Falls back to deriving from bjjmetricsHistory (historical, pre-live-tracking data)
    // whenever the live-tracked totals are empty — e.g. someone who competed only in
    // tournaments before we started live-tracking has totalTournaments: 0 even though
    // their bjjmetrics history clearly isn't empty.
    displayTotalTournaments() {
      if (this.profile.totalTournaments) {
        return this.profile.totalTournaments + this.unrepresentedActiveTournaments.length;
      }
      if (this.profile.bjjmetricsHistory && this.profile.bjjmetricsHistory.length > 0) {
        const uniqueTournaments = new Set(
          this.profile.bjjmetricsHistory.map((h) => h.tournament)
        );
        return uniqueTournaments.size + this.unrepresentedActiveTournaments.length;
      }
      return this.unrepresentedActiveTournaments.length;
    },
    displayMedalsByBelt() {
      if (this.profile.medalsByBelt && this.profile.medalsByBelt.length > 0) {
        return this.profile.medalsByBelt;
      }
      if (!this.profile.bjjmetricsHistory || this.profile.bjjmetricsHistory.length === 0) {
        return [];
      }
      const byBelt = {};
      for (const entry of this.profile.bjjmetricsHistory) {
        const belt = (entry.division || "").split("/")[0].trim() || "-";
        if (!byBelt[belt]) byBelt[belt] = { belt, gold: 0, silver: 0, bronze: 0 };
        if (entry.place === 1) byBelt[belt].gold++;
        else if (entry.place === 2) byBelt[belt].silver++;
        else if (entry.place === 3) byBelt[belt].bronze++;
      }
      return Object.values(byBelt);
    },
    totalMedals() {
      const totals = { gold: 0, silver: 0, bronze: 0 };
      for (const m of this.displayMedalsByBelt) {
        totals.gold += m.gold || 0;
        totals.silver += m.silver || 0;
        totals.bronze += m.bronze || 0;
      }
      totals.total = totals.gold + totals.silver + totals.bronze;
      return totals;
    },
  },
  methods: {
    goToTournament(tournament) {
      this.$router.push({
        name: "ibjjftournamentstourney",
        params: { tournamentId: tournament._id },
      });
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleDateString();
    },
  },
};
</script>
